import { Card, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { memo } from 'react';

interface ReminderCardProps<
  T extends { note: string; due_at: string; subject: string },
> {
  icon: string;
  isDismissed?: boolean;
  data: T;
}

function ReminderCard<
  T extends { note: string; due_at: string; subject: string },
>({ icon, isDismissed, data }: ReminderCardProps<T>) {
  const { note, due_at, subject } = data;

  return (
    <Card
      shadow='none'
      className='cursor-pointer rounded-lg bg-[#F4F4F5] hover:bg-gray-200'
    >
      <CardBody>
        <div className='flex items-center gap-4'>
          <Icon icon={icon} className='text-gray-600' width={24} />
          <div>
            <p
              className={`text-sm font-medium ${isDismissed ? 'text-default-500 line-through' : 'text-gray-900'}`}
            >
              {note}
            </p>
            {subject && (
              <div className='mt-1 flex items-center gap-2 text-sm text-default-600'>
                <Icon
                  icon='mdi:person-outline'
                  className='text-gray-600'
                  width={16}
                />
                {/* Person the comment was written about */}
                <span>{subject}</span>
              </div>
            )}
            <div className='mt-1 flex items-center gap-2 text-xs text-gray-500'>
              <span>{formatReminderDate(due_at)}</span>
              {isDismissed && <span className='text-success'>✓ Dismissed</span>}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default memo(ReminderCard);

function formatReminderDate(date: string): string {
  const reminderDate = new Date(date);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const timeString = reminderDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (reminderDate.toDateString() === today.toDateString()) {
    return `Today at ${timeString}`;
  } else if (reminderDate.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow at ${timeString}`;
  }

  return reminderDate.toLocaleString([], {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
